import {
  Box,
  getShadowByPath,
  Show,
  Space,
  Spinner,
  ThemeColorPath,
  ThemeIconKey,
  ThemeLottieKey,
  type ThemeShadowsPath,
} from 'platform/foundation';
import styled, {css} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {HTMLAttributeAnchorTarget, MouseEventHandler, useRef} from 'react';
import {Link, To} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

import {KeyboardShortcut} from '../../constants/KeyboardShortcut';
import {KEYBOARD_SHORTCUT_DEFINITIONS} from '../../constants/keyboardShortcutDefinitions';
import {useKeyboardShortcut} from '../../hooks/useKeyboardShortcut';
import {getKeyboardShortcutLabel} from '../../utils/getKeyboardShortcutLabel';
import {Badge, BadgeColorScheme} from '../Badge/Badge';
import {Tooltip} from '../Tooltip/Tooltip';
import {IconOrLottie} from './IconOrLottie';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'warning'
  | 'success'
  | 'outlined'
  | 'ghost'
  | 'ghostLink'
  | 'errorLink'
  | 'link'
  | 'dangerOutlined'
  | 'dangerGhost'
  | 'dangerLink';

export type ButtonSize = 'small' | 'default';

export type ButtonBoxShadow = Extract<ThemeShadowsPath, 'elevation_2'>;
export interface ButtonProps extends TestIdProps {
  title?: string;
  /** Shown after the title, typically in parentheses (e.g. vehicle count). Rendered with smaller text. */
  suffix?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  isLoading?: boolean;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  wrap?: boolean;
  leftIcon?: ThemeIconKey | ThemeLottieKey;
  rightIcon?: ThemeIconKey | ThemeLottieKey;
  badge?: {value: string; colorScheme: BadgeColorScheme};
  iconColor?: ThemeColorPath;
  type?: 'submit' | 'reset' | 'button';
  tabIndex?: number;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /**
   * Registers a global shortcut that invokes this button's click handler.
   * Shown in a tooltip as ⌘S / Ctrl+S (platform-specific).
   */
  keyboardShortcut?: KeyboardShortcut;
  /**
   * Button element HTML5 attribute used in FormButton
   */
  'aria-label'?: string;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  form?: string;
  to?: To;
  target?: HTMLAttributeAnchorTarget;
  replace?: boolean;
  boxShadow?: ButtonBoxShadow;
}

export function Button(props: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  useKeyboardShortcut({
    shortcut: props.keyboardShortcut,
    buttonRef,
    linkRef,
    isDisabled: props.isDisabled,
    isLoading: props.isLoading,
  });

  const shortcutLabel = isNotNil(props.keyboardShortcut)
    ? getKeyboardShortcutLabel(props.keyboardShortcut)
    : undefined;
  const ariaKeyShortcuts = isNotNil(props.keyboardShortcut)
    ? getSafely(KEYBOARD_SHORTCUT_DEFINITIONS, props.keyboardShortcut)?.ariaKeyShortcuts
    : undefined;

  const content = (
    <>
      <Show when={props.isLoading}>
        <Spinner variant="overlay" size="small" data-testid={suffixTestId('button', props)} />
      </Show>

      <ButtonIcon value={props.leftIcon} color={props.iconColor} paddingRight />

      {props.title}

      <Show when={props.suffix}>
        <Space horizontal={1} />
        <ButtonSuffix>{props.suffix}</ButtonSuffix>
      </Show>

      <Show when={props.badge}>
        <Space horizontal={2} />
        <Badge
          value={props.badge?.value ?? ''}
          colorScheme={props.badge?.colorScheme}
          size="small"
        />
      </Show>

      <ButtonIcon value={props.rightIcon} color={props.iconColor} paddingLeft />
    </>
  );

  const buttonElement = props.to ? (
    <StyledLink
      ref={linkRef}
      aria-label={props['aria-label']}
      aria-keyshortcuts={ariaKeyShortcuts}
      to={props.to}
      target={props.target}
      replace={props.replace}
      $isLoading={props.isLoading}
      $variant={props.variant}
      $size={props.size}
      $isFullWidth={props.isFullWidth}
      $boxShadow={props.boxShadow}
      $isFocused={props.isFocused}
      $isWrapped={props.wrap}
      tabIndex={props.tabIndex}
      data-testid={suffixTestId('button', props)}
      onClick={props.onClick}
    >
      {content}
    </StyledLink>
  ) : (
    <StyledButton
      ref={buttonRef}
      role="button"
      aria-label={props['aria-label']}
      aria-expanded={props['aria-expanded']}
      aria-controls={props['aria-controls']}
      aria-keyshortcuts={ariaKeyShortcuts}
      type={props.type ?? 'button'}
      disabled={props.isDisabled}
      $isLoading={props.isLoading}
      $variant={props.variant}
      $size={props.size}
      $boxShadow={props.boxShadow}
      $isFocused={props.isFocused}
      $isWrapped={props.wrap}
      form={props.form}
      tabIndex={props.tabIndex}
      onClick={props.onClick}
      $isFullWidth={props.isFullWidth}
      data-testid={suffixTestId('button', props)}
    >
      {content}
    </StyledButton>
  );

  if (isNil(shortcutLabel)) {
    return buttonElement;
  }

  return (
    <Tooltip label={shortcutLabel} shouldWrapperStretch={props.isFullWidth}>
      {buttonElement}
    </Tooltip>
  );
}

interface ButtonIconProps {
  value?: ThemeIconKey | ThemeLottieKey;
  color?: ThemeColorPath;
  paddingLeft?: boolean;
  paddingRight?: boolean;
}

function ButtonIcon(props: ButtonIconProps) {
  const icon = match(props.value)
    .with(Pattern.when(isNotNil), (value) => <IconOrLottie value={value} color={props.color} />)
    .otherwise(() => null);

  return (
    <Show when={props.value}>
      <Box
        paddingLeft={props.paddingLeft ? 1 : undefined}
        paddingRight={props.paddingRight ? 1 : undefined}
      >
        {icon}
      </Box>
    </Show>
  );
}

// eslint-disable-next-line eag/no-css-property
const baseStyles = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({theme}) => theme.fontSizes.text.small};
  font-weight: ${({theme}) => theme.fontWeights.regular};
  line-height: ${({theme}) => theme.lineHeights.text.small};
  letter-spacing: 0;
  white-space: nowrap;
  transition: all 250ms;

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const getVariantStyles = (variant?: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.accent};
        color: ${({theme}) => theme.colors.general.white};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.blue[70][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.blue[80][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.accent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.accent};
        }
      `;
    case 'secondary':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.palettes.neutral[30][100]};
        color: ${({theme}) => theme.colors.text.primary};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.neutral[40][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.neutral[50][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.palettes.neutral[30][100]};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.palettes.neutral[30][100]};
        }
      `;
    case 'outlined':
      return css`
        background: ${({theme}) => theme.colors.general.white};
        color: ${({theme}) => theme.colors.text.primary};
        border: 1px solid;
        border-color: ${({theme}) => theme.colors.palettes.neutral[70][100]};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.general.white};
          border: 1px solid;
          border-color: ${({theme}) => theme.colors.palettes.neutral[200][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.general.white};
          border: 1px solid;
          border-color: ${({theme}) => theme.colors.palettes.neutral[900][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.white};
          box-shadow: 0 0 0 1px ${({theme}) => theme.colors.palettes.neutral[70][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.white};
          border: 1px solid;
          border-color: ${({theme}) => theme.colors.palettes.neutral[70][100]};
        }
      `;
    case 'link':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.transparent};
        color: ${({theme}) => theme.components.Button.variants.link.color};
        border-radius: ${({theme}) => theme.getSize(1)};
        padding-inline: 0;

        &:hover {
          background: ${({theme}) => theme.colors.general.transparent};
          text-decoration: underline;
        }

        &:active {
          background: ${({theme}) => theme.colors.general.transparent};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.transparent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.transparent};
        }
      `;
    case 'ghost':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.transparent};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.components.Button.variants.ghost.hover.backgroundColor};
        }

        &:active {
          background: ${({theme}) => theme.components.Button.variants.ghost.active.backgroundColor};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.transparent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.transparent};
        }
      `;
    case 'ghostLink':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.transparent};
        color: ${({theme}) => theme.colors.text.link};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.neutral[30][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.neutral[40][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.transparent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.transparent};
        }
      `;
    case 'errorLink':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.transparent};
        color: ${({theme}) => theme.colors.text.danger};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.general.transparent};
          text-decoration: underline;
        }

        &:active {
          background: ${({theme}) => theme.colors.general.transparent};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.transparent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.transparent};
        }
      `;
    case 'danger':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.severity.danger};
        color: ${({theme}) => theme.colors.general.white};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.red[70][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.red[80][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.severity.danger};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.severity.danger};
        }
      `;
    case 'warning':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.severity.warning};
        color: ${({theme}) => theme.colors.general.white};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.orange[70][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.orange[80][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.severity.warning};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.severity.warning};
        }
      `;
    case 'success':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.severity.success};
        color: ${({theme}) => theme.colors.general.white};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.green[70][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.green[80][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.severity.danger};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.severity.danger};
        }
      `;
    case 'dangerOutlined':
      return css`
        background: ${({theme}) => theme.colors.general.white};
        color: ${({theme}) => theme.colors.text.danger};
        border: 1px solid;
        border-color: ${({theme}) => theme.colors.severity.danger};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.general.white};
          border: 1px solid;
          border-color: ${({theme}) => theme.colors.palettes.red[80][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.general.white};
          border: 1px solid;
          border-color: ${({theme}) => theme.colors.palettes.red[90][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.white};
          box-shadow: 0 0 0 1px ${({theme}) => theme.colors.severity.danger};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.white};
          border: 1px solid;
          border-color: ${({theme}) => theme.colors.severity.danger};
        }
      `;
    case 'dangerLink':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.transparent};
        color: ${({theme}) => theme.colors.text.danger};
        border-radius: ${({theme}) => theme.getSize(1)};
        padding: 0;

        &:hover {
          background: ${({theme}) => theme.colors.general.transparent};
          text-decoration: underline;
        }

        &:active {
          background: ${({theme}) => theme.colors.general.transparent};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.transparent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.transparent};
        }
      `;
    case 'dangerGhost':
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.transparent};
        color: ${({theme}) => theme.colors.text.danger};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.neutral[30][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.neutral[40][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.transparent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.transparent};
        }
      `;
    default:
      // eslint-disable-next-line eag/no-css-property
      return css`
        background: ${({theme}) => theme.colors.general.accent};
        color: ${({theme}) => theme.colors.general.white};
        border-radius: ${({theme}) => theme.getSize(1)};

        &:hover {
          background: ${({theme}) => theme.colors.palettes.blue[70][100]};
        }

        &:active {
          background: ${({theme}) => theme.colors.palettes.blue[80][100]};
        }

        &:focus-visible {
          background: ${({theme}) => theme.colors.general.accent};
          box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
        }

        &:disabled {
          background: ${({theme}) => theme.colors.general.accent};
        }
      `;
  }
};

const getSizeStyles = (size?: ButtonSize) => {
  switch (size) {
    case 'default':
      // eslint-disable-next-line eag/no-css-property
      return css`
        height: ${({theme}) => theme.getSize(8)};
        padding-block: 6px;
        padding-inline: ${({theme}) => theme.getSize(3)};
      `;
    case 'small':
      // eslint-disable-next-line eag/no-css-property
      return css`
        height: ${({theme}) => theme.getSize(6)};
        padding-block: 2px;
        padding-inline: ${({theme}) => theme.getSize(2)};
      `;
    default:
      // eslint-disable-next-line eag/no-css-property
      return css`
        height: ${({theme}) => theme.getSize(8)};
        padding-block: 6px;
        padding-inline: ${({theme}) => theme.getSize(3)};
      `;
  }
};

const getCombinedSizeVariantStyles = (size?: ButtonSize, variant?: ButtonVariant) => {
  if (size === 'small' && variant === 'link') {
    // eslint-disable-next-line eag/no-css-property
    return css`
      font-size: ${({theme}) => theme.fontSizes.text.xSmall};
      line-height: ${({theme}) => theme.lineHeights.text.xSmall};
      height: ${({theme}) => theme.getSize(4)};
    `;
  }
  return '';
};

// eslint-disable-next-line eag/no-css-property
const ButtonSuffix = styled.span`
  color: inherit;
  font-size: ${({theme}) => theme.fontSizes.text.xSmall};
  line-height: ${({theme}) => theme.lineHeights.text.xSmall};
`;

interface SharedStyleProps {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $isFullWidth?: boolean;
  $isLoading?: boolean;
  $boxShadow?: ButtonBoxShadow;
  $isFocused?: boolean;
  $isWrapped?: boolean;
}

const sharedStyles = css<SharedStyleProps>`
  ${baseStyles}
  ${({$size}) => getSizeStyles($size)}
  ${({$variant}) => getVariantStyles($variant)}
  ${({$size, $variant}) => getCombinedSizeVariantStyles($size, $variant)}

  ${(props) =>
    props.$isLoading &&
    css`
      pointer-events: none;
      overflow: hidden;
    `}

  ${(props) =>
    props.$isFullWidth &&
    css`
      min-width: 100%;
    `}

  ${(props) =>
    props.$boxShadow &&
    css`
      box-shadow: ${getShadowByPath(props.$boxShadow, props.theme)};
    `}

  ${(props) =>
    props.$isFocused &&
    css`
      outline: ${props.theme.outline.default} ${props.theme.colors.general.accent};
      outline-offset: ${props.theme.outline.offset};
    `}

  ${(props) =>
    props.$isWrapped &&
    css`
      height: auto;
      max-width: 100%;
      min-width: ${props.$isFullWidth ? '100%' : 0};
      white-space: normal;
      overflow-wrap: anywhere;
      text-align: left;
    `}
`;

const StyledButton = styled.button<SharedStyleProps>`
  ${sharedStyles}
`;

const StyledLink = styled(Link)<SharedStyleProps>`
  ${sharedStyles}
`;
